import React from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { radius, SurfacePalette, type } from '../../../tokens'
import { useSectionNumber } from '../../PacketSectionsContext'
import { PresentPage } from '../PresentPage'

interface Props {
  palette: SurfacePalette
  packetID: number
  surveyLink: string
  answered: boolean
}

// Present-mode survey slide: the survey QR, and once someone has answered
// through it, a line saying so. Never a count or what they said — the client
// answers honestly only if the room can't see it. Read-only, so no
// PresentNavLock.
//
// Takes the link as a plain `string`, not the nullable status: a packet with no
// link has no survey slide at all, so the host filters it out of the deck and
// this component never has to render an empty state.
export const PresentClientSurveySection = ({ palette, packetID, surveyLink, answered }: Props) => (
  <PresentPage
    palette={palette}
    number={useSectionNumber('client-survey')}
    title="Client Survey"
    accentWord="Survey"
    commentAnchor={{ type: 'section', id: `${packetID}:client-survey`, label: 'Client Survey' }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 56,
        paddingTop: 12,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          padding: 20,
          borderRadius: radius.lg,
          background: '#fff',
        }}
      >
        <QRCodeSVG value={surveyLink} size={280} />
      </div>
      <div style={{ maxWidth: 440 }}>
        <div
          style={{
            fontFamily: type.family.serif,
            fontSize: type.size.h2,
            color: palette.text,
            marginBottom: 8,
          }}
        >
          Scan to share your feedback
        </div>
        <p
          style={{
            margin: 0,
            fontFamily: type.family.sans,
            fontSize: type.size.body,
            color: palette.textMuted,
            lineHeight: 1.5,
          }}
        >
          Answer privately on your own phone — your answers go to the leadership team overseeing
          your account and never appear on this screen.
        </p>
        {answered && (
          <div
            style={{
              marginTop: 16,
              fontFamily: type.family.sans,
              fontSize: type.size.body,
              color: palette.status.success,
            }}
          >
            ✓ Thanks — your answers are in.
          </div>
        )}
      </div>
    </div>
  </PresentPage>
)
